import { useEffect } from "react";
import { createPortal } from "react-dom";
import CoinAmount from "./CoinAmount.jsx";

function ParticipantWindow({ visible, participant, onClose }) {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!visible) return;
      if (event.key === "Escape" && typeof onClose === "function") {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [visible, onClose]);

  if (!visible || !participant) {
    return null;
  }

  return createPortal(
    <div>
      <style>{`
        .participant-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(64, 200, 255, 0.82) rgba(2, 10, 26, 0.92);
        }

        .participant-scroll::-webkit-scrollbar {
          width: 10px;
        }

        .participant-scroll::-webkit-scrollbar-track {
          background: linear-gradient(180deg, rgba(2, 10, 26, 0.96), rgba(5, 18, 40, 0.94));
          border-radius: 999px;
        }

        .participant-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, rgba(64, 224, 255, 0.95), rgba(18, 125, 190, 0.95));
          border: 1px solid rgba(140, 235, 255, 0.32);
          border-radius: 999px;
        }

        .participant-scroll::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, rgba(102, 238, 255, 0.98), rgba(25, 156, 216, 0.98));
        }
      `}</style>

      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(2, 10, 30, 0.75)",
          backdropFilter: "blur(4px)",
          zIndex: 1001,
        }}
        onClick={onClose}
      />

      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1003,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "relative",
            border: "1px solid rgba(80, 180, 255, 0.4)",
            borderRadius: 20,
            boxShadow: "0 0 40px rgba(0, 150, 255, 0.25), 0 8px 24px rgba(0, 0, 0, 0.7)",
            padding: "20px 22px",
            width: "min(860px, 96vw)",
            maxHeight: "90vh",
            boxSizing: "border-box",
            background: "linear-gradient(160deg, rgba(10, 35, 70, 0.97) 0%, rgba(5, 20, 50, 0.98) 100%)",
            backdropFilter: "blur(16px)",
            pointerEvents: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: 10,
              right: 12,
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: "1px solid rgba(144, 224, 239, 0.6)",
              background: "radial-gradient(circle at 30% 0%, #012a4a, #001219)",
              color: "#e0f7fa",
              fontWeight: "bold",
              fontSize: 16,
              cursor: "pointer",
              boxShadow: "0 3px 9px rgba(0, 0, 0, 0.7)",
            }}
            aria-label="Close participant window"
          >
            ×
          </button>

          <div style={{ fontWeight: 800, fontSize: 22, color: "#40e8ff", paddingRight: 34 }}>
            {participant.name}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <span className="login-help-text" style={{ padding: "6px 10px", borderRadius: 999, background: "rgba(15, 60, 110, 0.7)", color: "#dff8ff", margin: 0 }}>
              Rank #{participant.rank}
            </span>
            <span className="login-help-text" style={{ padding: "6px 10px", borderRadius: 999, background: "rgba(90, 70, 10, 0.55)", color: "#ffea9a", margin: 0 }}>
              {participant.wins} wins
            </span>
            <span className="login-help-text" style={{ padding: "6px 10px", borderRadius: 999, background: "rgba(10, 28, 55, 0.78)", color: "#eefbff", margin: 0 }}>
              {participant.gamesParticipated} games
            </span>
            <span className="login-help-text" style={{ padding: "6px 10px", borderRadius: 999, background: "rgba(10, 28, 55, 0.78)", color: "#eefbff", margin: 0 }}>
              {participant.attendance.toFixed(1)}% attendance
            </span>
            <span className="login-help-text" style={{ padding: "6px 10px", borderRadius: 999, background: "rgba(10, 28, 55, 0.78)", color: "#eefbff", margin: 0 }}>
              {participant.winRate.toFixed(1)}% win rate
            </span>
            <span className="login-help-text" style={{ padding: "6px 10px", borderRadius: 999, background: "rgba(22, 54, 18, 0.72)", color: "#ddffd1", margin: 0, display: "inline-flex", alignItems: "center", gap: 6 }}>
              Total value <CoinAmount value={participant.totalValue} compact color="#ddffd1" />
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
            <section
              style={{
                borderRadius: 12,
                border: "1px solid rgba(144,224,239,0.2)",
                background: "rgba(8, 24, 50, 0.6)",
                padding: 12,
                minHeight: 160,
              }}
            >
              <div style={{ fontWeight: 700, color: "#90e0ef", marginBottom: 8 }}>Games participated in</div>
              <div className="participant-scroll" style={{ maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                {participant.games.length > 0 ? (
                  participant.games.map((gameTitle, index) => (
                    <div
                      key={`${participant.key}-game-${index}`}
                      style={{
                        padding: "7px 9px",
                        borderRadius: 10,
                        background: "rgba(10, 28, 55, 0.78)",
                        border: "1px solid rgba(144,224,239,0.16)",
                        color: "#eefbff",
                        fontSize: 13,
                      }}
                    >
                      {gameTitle}
                    </div>
                  ))
                ) : (
                  <div className="login-help-text" style={{ margin: 0 }}>
                    No game participation data.
                  </div>
                )}
              </div>
            </section>

            <section
              style={{
                borderRadius: 12,
                border: "1px solid rgba(144,224,239,0.2)",
                background: "rgba(8, 24, 50, 0.6)",
                padding: 12,
                minHeight: 160,
              }}
            >
              <div style={{ fontWeight: 700, color: "#90e0ef", marginBottom: 8 }}>Prizes won</div>
              <div className="participant-scroll" style={{ maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                {participant.prizes.length > 0 ? (
                  participant.prizes.map((prize, index) => (
                    <div
                      key={`${participant.key}-prize-${prize.itemId || prize.itemName || "unknown"}-${index}`}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "44px minmax(0, 1fr)",
                        gap: 10,
                        alignItems: "center",
                        padding: "7px 9px",
                        borderRadius: 10,
                        background: "rgba(10, 28, 55, 0.78)",
                        border: "1px solid rgba(144,224,239,0.16)",
                      }}
                    >
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 10,
                          background: "rgba(10, 16, 30, 0.92)",
                          border: "1px solid rgba(144,224,239,0.24)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {prize.icon ? (
                          <img src={prize.icon} alt={prize.itemName || "Prize"} style={{ width: 30, height: 30, objectFit: "contain" }} />
                        ) : null}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            color: "#eefbff",
                            fontWeight: 700,
                            fontSize: 13,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={prize.itemName || "Unknown item"}
                        >
                          {prize.itemName || "Unknown item"}
                        </div>
                        <div style={{ display: "grid", gap: 2, marginTop: 4 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#cbe8ff", fontSize: 11 }}>
                            <span>Buy</span>
                            <CoinAmount value={prize.tpBuyPrice} compact color="#cbe8ff" />
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#cbe8ff", fontSize: 11 }}>
                            <span>Sell</span>
                            <CoinAmount value={prize.tpSellPrice} compact color="#cbe8ff" />
                          </div>

                          {(prize.quantity || 1) !== 1 && (
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#ddffd1", fontSize: 11 }}>
                              <span>Sell total</span>
                              <CoinAmount value={prize.totalSellValue} compact color="#ddffd1" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="login-help-text" style={{ margin: 0 }}>
                    No prize wins recorded.
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ParticipantWindow;
